/**
 * @file src/api/arrangementRevisions.js
 * @description Makes an arrangement write say what CHANGED, and say which write it is.
 *
 * ## The two problems, which are one problem
 *
 * A studio arrangement is saved by replacing the whole stored layout. Nudge one divider and the
 * entire card rail goes with it — up to 500 collection-item ids — to say that one number moved. And
 * because the writes are fire-and-forget, two of them in flight can reach the server out of order,
 * where an unconditional replace means the OLDER one wins and the newer arrangement is lost.
 *
 * Both come from the write being a REPLACEMENT rather than a statement of what changed. A write
 * that named only the changed fields would be small, and could not clobber fields it does not
 * mention — but on its own it still could not tell a stale write from a fresh one, because nothing
 * in it says WHEN it was made. So the two halves are fixed together: a delta, plus a revision the
 * server can compare.
 *
 * ## The revision
 *
 * A plain counter, held by this side, sent inside the layout and stored with it. It starts from
 * whatever revision the boot READ found (see `observeStored`) and goes up by one per write, so the
 * writes of one studio session are strictly ordered no matter what order they arrive in. The server
 * applies a write only when its revision is at least the stored one, and answers with the revision
 * it ended up holding.
 *
 * Equal revisions are REFUSED. Within one session revisions strictly increase, so an equal pair can
 * only come from two devices that read the same arrangement and each changed something. Letting
 * both land looks tempting when they name different fields — but it combines POSITIONS taken from
 * two different bases, and the record ends up holding one device's stage order with the other's
 * swaps pointing at cards that are no longer at those indices: a layout neither device ever had.
 * One of them is told it is behind instead, and re-states its whole arrangement above the revision
 * it was given.
 *
 * ## Why the delta is measured against what the server CONFIRMED
 *
 * Not against the last thing sent. A write that failed never reached the server, so a delta built
 * on top of it would describe fields the stored record never got — lost for good, since nothing
 * retries. Measuring from the last CONFIRMED state costs a few redundant fields while a write is in
 * flight and is unconditionally correct: every field that changed since the server last agreed with
 * us is in the next write, however many attempts it takes.
 *
 * It is also what makes the two-in-flight case safe. A delta is only ever built on a base the
 * server has already acknowledged, so a delta can never be applied on top of a write that is still
 * airborne — the one ordering hazard a revision alone would not catch.
 *
 * ## Why the first write of a session is always a full replace
 *
 * The boot read is allowed to come from the offline mirror, which can be BEHIND the server. A delta
 * measured against a stale mirror would leave the stored record holding a mixture of two
 * arrangements. So a session states the whole arrangement once, and only then starts sending
 * deltas — against a base the server itself has confirmed.
 *
 * ## Why the delta is a different VERB, not a flag
 *
 * A server that has not been deployed yet would read a flagged delta as a whole arrangement and
 * store it as one, erasing every field the delta did not mention. So a delta is a PATCH to its own
 * route, which such a server does not have: it refuses the request instead of misreading it, and the
 * caller falls back to stating the whole arrangement.
 *
 * That is why the capability is not latched here. A flag would have to be trusted once and for ever,
 * and a rolling deployment or a ROLLBACK would then send a delta to a server that eats it. A verb is
 * re-proved by every single request, which is the only version check that cannot go stale.
 *
 * ## Why `confirmed` is what the SERVER echoed, not what this side sent
 *
 * The two are usually identical and occasionally are not: another device may have moved the record
 * since, so a delta lands on an arrangement this side never saw. The answer carries the layout as
 * STORED, and adopting that is what makes the next delta a statement about the record rather than
 * about a belief — including in the mirror, which would otherwise keep a value the server never held.
 */

/**
 * A revision the client is willing to send. Bounded so a client cannot write a number that no later
 * revision can exceed and permanently freeze the record. Far above any real session's count.
 */
const MAX_REVISION = 2_000_000_000;

/** The layout fields a delta may name. Anything else on a layout object is not part of the record. */
const LAYOUT_FIELDS = [
  'subtype',
  'splitY',
  'splitXPrimary',
  'splitXSecondary',
  'focusedIndex',
  'drawerOpen',
  'drawerHeight',
  'order',
  'entryOrder',
  'swaps',
];

/** A revision as it may be trusted from a response or a stored layout. */
function readRevision(value) {
  if (!Number.isInteger(value) || value < 0 || value > MAX_REVISION) return null;
  return value;
}

/**
 * Whether two layout field values are the same arrangement fact.
 *
 * Structural rather than referential: every write rebuilds the layout from scratch, so the arrays
 * are new objects on every gesture and a reference check would call every field changed and send
 * the whole record every time — the exact thing this exists to stop.
 *
 * Deliberately narrow: layout values are numbers, booleans, arrays of ids-or-null, and the sparse
 * `swaps` array of small flat objects. Anything unexpected compares as CHANGED, so an unknown shape
 * is re-sent rather than silently dropped from a delta.
 */
function sameValue(a, b) {
  if (a === b) return true;
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;
    return a.every((item, index) => sameValue(item, b[index]));
  }
  if (a && b && typeof a === 'object' && typeof b === 'object' && !Array.isArray(a) && !Array.isArray(b)) {
    const keysA = Object.keys(a);
    const keysB = Object.keys(b);
    if (keysA.length !== keysB.length) return false;
    return keysA.every((key) => sameValue(a[key], b[key]));
  }
  return false;
}

/** The layout's own fields, with anything the record does not hold left out. */
function pickLayout(layout) {
  const out = {};
  for (const field of LAYOUT_FIELDS) {
    if (layout[field] !== undefined) out[field] = layout[field];
  }
  return out;
}

/**
 * The fields of `next` that differ from `base`, plus any field an UNCONFIRMED write already named.
 *
 * That second half is not belt and braces — without it a field can be left holding a value nobody
 * asked for. A divider dragged away and then back while the first save is still in flight differs
 * from the confirmed base by nothing, so the second write would carry nothing, so the first write
 * lands unopposed and stores the value the person moved off. Re-stating every field an outstanding
 * write touched, at its CURRENT value, makes the newer write always say at least as much as the one
 * it may overtake — which is what its higher revision promises.
 *
 * A field the base HAS and the next one does not is included as `null` rather than omitted: a delta
 * says what the record should hold, and an omitted field means "unchanged" — so an arrangement that
 * genuinely lost a field would keep it for ever. In practice `currentLayout()` always states every
 * field it owns, so this is a floor under a shape change, not a live case.
 */
function diffLayout(base, next, pending) {
  const delta = {};
  for (const field of LAYOUT_FIELDS) {
    const before = base[field];
    const after = next[field];
    if (after === undefined) {
      if (before !== undefined) delta[field] = null;
      continue;
    }
    if (!sameValue(before, after) || pending?.has(field)) delta[field] = after;
  }
  // A focus is only meaningful against a stage COUNT, so a delta that moves one states the other.
  // Without this the server would have to read the stored layout to clamp — a query, and a racy one:
  // the count it read can be changed by another write in the gap before this one applies, storing a
  // focus that was valid when it was read and past the last stage by the time it landed.
  if (delta.focusedIndex !== undefined && delta.subtype === undefined && next.subtype !== undefined) {
    delta.subtype = next.subtype;
  }
  return delta;
}

/**
 * One record's write state: what the server last agreed it holds, and which revision that was.
 *
 * @typedef {{
 *   confirmed: object|null,   // the last layout the server accepted from us, or null for "unknown"
 *   revision: number,         // the last revision ISSUED (not necessarily accepted)
 *   issued: number,           // the highest revision currently in flight or done — supersession check
 *   pending: Set<string>,     // fields named by writes the server has not confirmed yet
 *   mirrored: number,         // the highest revision written to the offline mirror
 * }} RecordState
 */

/**
 * Track the writes of ONE kind of record — the collection's own layout, or a person's own
 * arrangement — keyed by collection id. Each kind gets its own tracker because each is a different
 * document with its own revision line; sharing one would let a write to the collection's layout
 * decide whether a write to somebody's personal arrangement was stale.
 *
 * @returns {{
 *   observeStored: (key: string, layout: object|null) => void,
 *   begin: (key: string, layout: object) => ({revision:number, layout:object, partial:boolean, full:object}|null),
 *   confirm: (key: string, write: object, storedLayout: object|null) => void,
 *   reject: (key: string, write: object, serverRevision: number|null) => boolean,
 *   fail: (key: string, write: object) => void,
 *   forget: (key: string) => void,
 *   claimMirror: (key: string, write: object) => boolean,
 * }}
 */
export function createArrangementRevisions() {
  /** @type {Map<string, RecordState>} */
  const records = new Map();

  function stateFor(key) {
    let state = records.get(key);
    if (!state) {
      state = { confirmed: null, revision: 0, issued: 0, pending: new Set(), mirrored: 0 };
      records.set(key, state);
    }
    return state;
  }

  return {
    /**
     * Seed this record's revision line from a layout that was READ.
     *
     * Only the NUMBER is taken, never the layout as a confirmed base: the read may have come from
     * the offline mirror, which can be behind the server, and a delta measured against a stale base
     * would store a mixture of two arrangements. The number is safe on its own — the worst a stale
     * one does is make the session's first write look older than the record, which the server
     * answers with its own revision and this side corrects from.
     */
    observeStored(key, layout) {
      if (!key) return;
      const revision = readRevision(layout?.rev);
      if (revision === null) return;
      const state = stateFor(key);
      // Never DOWN: two reads of the same collection in one session (a re-entered studio) must not
      // walk the counter backwards behind writes that have already gone out on it.
      //
      // Only the counter. `issued` marks which write is the newest one OUTSTANDING, and a read is
      // not a write — moving it would make a save still in flight look overtaken by nothing, and
      // its answer would be discarded.
      state.revision = Math.max(state.revision, revision);
    },

    /**
     * Open a write: the body to send, and the revision it carries. `null` when the arrangement is
     * already exactly what the server confirmed — a settled divider that landed back where it
     * started, or a geometry callback that fired with nothing new in it, is not worth a request.
     */
    begin(key, layout, { whole = false } = {}) {
      if (!key || !layout || typeof layout !== 'object') return null;
      const state = stateFor(key);
      const full = pickLayout(layout);

      // Past the cap this side stops claiming a revision at all, rather than repeating the maximum.
      // Two writes carrying the same number cannot be ordered against each other, which is the whole
      // failure this exists to prevent — so it degrades to the unconditional whole-arrangement write
      // this shipped as before. Unreachable in any real session; a floor, not a case.
      if (state.revision >= MAX_REVISION) {
        return { revision: null, layout: full, partial: false, full };
      }

      // A delta needs a base the server has confirmed. Whether the SERVER can take one is not
      // latched here — it is a different verb, and every request re-proves it (see the file note).
      //
      // `whole` is the LEAVING write asking for a replace regardless. A delta's verb is refused by a
      // server that predates it, and the caller recovers by re-stating the whole arrangement — but
      // the exit write has no second chance: the document is going, and a request that comes back
      // refused has nobody left to answer it. So the one write that cannot be retried does not
      // gamble on a route, and states everything.
      const canDelta = !whole && state.confirmed !== null;
      const body = canDelta ? diffLayout(state.confirmed, full, state.pending) : full;
      if (canDelta && Object.keys(body).length === 0) return null;

      const revision = state.revision + 1;
      state.revision = revision;
      state.issued = revision;
      for (const field of Object.keys(body)) state.pending.add(field);
      return { revision, layout: { ...body, rev: revision }, partial: canDelta, full };
    },

    /**
     * The server applied this write and answered with the arrangement it now holds.
     *
     * `storedLayout` is that answer, and it becomes the base — not what this side sent. Usually the
     * two are the same; when another device has moved the record they are not, and adopting what the
     * server actually holds is what keeps the next delta a statement about the record. A server that
     * answers with nothing usable leaves the base UNKNOWN rather than assuming, so the next write
     * states the whole arrangement.
     */
    confirm(key, write, storedLayout) {
      if (!key || !write) return;
      const state = stateFor(key);
      // A LATER write has already been issued from this base, so its own answer decides the base.
      // Confirming an overtaken write would move `confirmed` backwards and make the next delta
      // describe fields that had already moved on.
      if (write.revision !== state.issued) return;
      const echoed = storedLayout && typeof storedLayout === 'object' ? pickLayout(storedLayout) : null;
      state.confirmed = echoed && Object.keys(echoed).length ? echoed : null;
      const echoedRevision = readRevision(storedLayout?.rev);
      if (echoedRevision !== null) state.revision = Math.max(state.revision, echoedRevision);
      // Nothing is outstanding any more. Safe by construction: a write measured with `pending` names
      // every field an earlier unconfirmed write named, so this base speaks for all of them.
      state.pending.clear();
    },

    /**
     * The server refused this write as STALE and told us the revision it holds. Returns whether the
     * caller should re-issue.
     *
     * A single correction, not a queue: the base was wrong, so the delta rules out nothing and the
     * next write must be a full replace above the server's revision. Skipped when a newer write has
     * already been issued for this record — that one measures from the same confirmed base and
     * therefore already carries everything this one did.
     */
    reject(key, write, serverRevision) {
      if (!key || !write) return false;
      const state = stateFor(key);
      state.confirmed = null;
      // The next write states the whole arrangement, which covers every outstanding field.
      state.pending.clear();
      const known = readRevision(serverRevision);
      if (known !== null) state.revision = Math.max(state.revision, known);
      return write.revision === state.issued;
    },

    /** Drop the base without touching the counter — a refusal this side will not answer. */
    unconfirm(key) {
      if (!key) return;
      const state = stateFor(key);
      state.confirmed = null;
      state.pending.clear();
    },

    /**
     * The write did not get through — offline, refused, a network error, a 500. The base is now
     * unknown, so the next write states the whole arrangement again rather than a delta measured
     * against something the server may never have received.
     */
    fail(key, write) {
      if (!key || !write) return;
      const state = stateFor(key);
      if (write.revision !== state.issued) return;
      state.confirmed = null;
      state.pending.clear();
    },

    /**
     * Claim the offline mirror for this write, or refuse it.
     *
     * The mirror is written AFTER the server accepts, so two accepted writes can reach this point
     * in either order and the older one would leave the mirror holding an arrangement the server
     * has already moved past — the same reordering as the writes themselves, one layer down, and
     * the mirror is what a boot with no network reads.
     *
     * A high-water mark rather than "is this the newest write issued": a write that was overtaken
     * but ACCEPTED still describes the record better than whatever the mirror held before, and if
     * the newer one turns out to fail there is nothing else coming to write it.
     */
    claimMirror(key, write) {
      if (!key || !write) return false;
      // A write made past the revision cap carries no revision to order by (see `begin`). Nothing
      // can be said about where it sits, so it is neither held back nor recorded as a mark.
      if (!Number.isInteger(write.revision)) return true;
      const state = stateFor(key);
      if (state.mirrored >= write.revision) return false;
      state.mirrored = write.revision;
      return true;
    },

    /**
     * Drop everything known about a record, because it no longer exists. The editor path retires a
     * person's own arrangement server-side as part of saving the collection's; a base and a
     * revision left over from the retired row would describe a document that is gone, and the next
     * personal write would send a delta against it.
     */
    forget(key) {
      if (key) records.delete(key);
    },
  };
}

export const __TESTING__ = { MAX_REVISION, LAYOUT_FIELDS, diffLayout, sameValue };
